import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Card, Form, Select, InputNumber, DatePicker, Input, Button,
  Table, Space, message, Slider, Typography, Divider, Upload,
} from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractApi, customerApi, productApi, quotationApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const CONTRACT_TYPES = ['产品销售合同', '工程合同', '运维合同', '框架协议'];

export default function ContractForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromQuotationId = searchParams.get('fromQuotation');

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  // 合同项目
  const [items, setItems] = useState([{
    key: 1, productId: null, name: '', spec: '',
    quantity: 1, unit: '台', unitPrice: 0, subtotal: 0,
  }]);

  // 付款计划
  const [paymentPlan, setPaymentPlan] = useState([{
    key: 1, percentage: 100, amount: 0, method: '银行转账',
    plannedDate: null, notes: '',
  }]);

  // 附件
  const [attachment, setAttachment] = useState('');

  useEffect(() => {
    customerApi.list({ pageSize: 200 }).then((r) => setCustomers(r.data)).catch(() => {});
    productApi.list({ pageSize: 500 }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // 如果从报价转化，预填数据
  useEffect(() => {
    if (!fromQuotationId) return;
    quotationApi.get(fromQuotationId).then((q) => {
      form.setFieldsValue({
        customerId: q.customerId,
        type: {
          '产品销售': '产品销售合同', '项目工程': '工程合同', '运维服务': '运维合同',
        }[q.type] || '产品销售合同',
        notes: q.notes || `由报价 ${q.code} 转化`,
      });
      setItems(q.items.map((it, idx) => ({
        key: idx + 1,
        productId: it.productId,
        name: it.name,
        spec: it.spec,
        quantity: it.quantity,
        unit: it.unit,
        unitPrice: it.unitPrice,
        subtotal: it.subtotal,
      })));
      setPaymentPlan([{
        key: 1, percentage: 100, amount: q.totalAmount, method: '银行转账',
        plannedDate: null, notes: '',
      }]);
    }).catch((e) => {
      message.error('获取报价信息失败: ' + (e?.error || ''));
    });
  }, [fromQuotationId, form]);

  // 产品项目总价
  const itemsTotal = useMemo(() => {
    return Math.round(items.reduce((s, i) => {
      return s + (i.quantity || 0) * (i.unitPrice || 0);
    }, 0) * 100) / 100;
  }, [items]);

  // 付款分配联动
  const updatePaymentAmounts = useCallback((plan) => {
    return plan.map((p) => ({
      ...p,
      amount: Math.round((p.percentage / 100) * itemsTotal * 100) / 100,
    }));
  }, [itemsTotal]);

  const onProductSelect = (productId, key) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    setItems((prev) => prev.map((item) => {
      if (item.key !== key) return item;
      return {
        ...item, productId,
        name: product.name,
        spec: product.spec || '',
        unit: product.unit || '台',
        unitPrice: product.listPrice || 0,
        quantity: 1,
      };
    }));
  };

  const onItemChange = (key, field, value) => {
    setItems((prev) => prev.map((item) => {
      if (item.key !== key) return item;
      return { ...item, [field]: value };
    }));
  };

  const addItemRow = () => {
    const maxKey = Math.max(...items.map((i) => i.key), 0);
    setItems((prev) => [...prev, {
      key: maxKey + 1, productId: null, name: '', spec: '',
      quantity: 1, unit: '台', unitPrice: 0, subtotal: 0,
    }]);
  };

  const removeItemRow = (key) => {
    if (items.length <= 1) { message.warning('至少保留一个项目'); return; }
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const onPaymentChange = (key, field, value) => {
    setPaymentPlan((prev) => {
      const updated = prev.map((p) => {
        if (p.key !== key) return p;
        return { ...p, [field]: value };
      });
      return updatePaymentAmounts(updated);
    });
  };

  const addPaymentRow = () => {
    const maxKey = Math.max(...paymentPlan.map((p) => p.key), 0);
    const remaining = 100 - paymentPlan.reduce((s, p) => s + p.percentage, 0);
    const newPct = Math.max(0, remaining);
    setPaymentPlan((prev) => updatePaymentAmounts([...prev, {
      key: maxKey + 1, percentage: newPct, amount: 0,
      method: '银行转账', plannedDate: null, notes: '',
    }]));
  };

  const removePaymentRow = (key) => {
    if (paymentPlan.length <= 1) { message.warning('至少保留一个付款阶段'); return; }
    setPaymentPlan((prev) => {
      const filtered = prev.filter((p) => p.key !== key);
      return updatePaymentAmounts(filtered);
    });
  };

  const handleSubmit = async (values) => {
    if (items.length === 0 || !items.filter((i) => i.name).length) {
      message.warning('请添加产品项目'); return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customerId: values.customerId,
        type: values.type,
        signedAt: values.signedAt ? values.signedAt.toISOString() : null,
        startAt: values.startAt ? values.startAt.toISOString() : null,
        endAt: values.endAt ? values.endAt.toISOString() : null,
        sourceFrom: fromQuotationId,
        notes: values.notes || '',
        attachment: attachment || null,
        items: items.filter((i) => i.name).map((i) => ({
          productId: i.productId,
          name: i.name,
          spec: i.spec,
          quantity: i.quantity || 1,
          unit: i.unit || '台',
          unitPrice: i.unitPrice || 0,
          subtotal: (i.quantity || 1) * (i.unitPrice || 0),
        })),
      };

      const contract = await contractApi.create(payload);

      // 创建付款计划
      if (paymentPlan.length > 0 && itemsTotal > 0) {
        const validPayments = paymentPlan.filter((p) => p.amount > 0);
        for (const p of validPayments) {
          await contractApi.addPayment(contract.id, {
            amount: p.amount,
            method: p.method || '银行转账',
            paidAt: p.plannedDate ? p.plannedDate.toISOString() : null,
            notes: p.notes || '',
          }).catch(() => {}); // 付款添加失败不阻止整体流程
        }
      }

      message.success('合同创建成功');
      navigate(`/contracts/${contract.id}`);
    } catch (e) {
      message.error(e?.error || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const itemColumns = [
    {
      title: '产品', key: 'product', width: 200,
      render: (_, r) => (
        <Select showSearch placeholder="搜索选择产品"
          value={r.productId || undefined}
          onChange={(v) => onProductSelect(v, r.key)}
          filterOption={(input, option) =>
            (option.children || '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }} allowClear
        >
          {products.map((p) => (
            <Select.Option key={p.id} value={p.id}>{`${p.name} [${p.sku}]`}</Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: '规格', key: 'spec', width: 100,
      render: (_, r) => (
        <Input value={r.spec}
          onChange={(e) => onItemChange(r.key, 'spec', e.target.value)} placeholder="规格" />
      ),
    },
    {
      title: '数量', key: 'quantity', width: 80,
      render: (_, r) => (
        <InputNumber min={1} value={r.quantity}
          onChange={(v) => onItemChange(r.key, 'quantity', v || 1)}
          style={{ width: '100%' }} />
      ),
    },
    {
      title: '单位', key: 'unit', width: 60,
      render: (_, r) => (
        <Input value={r.unit}
          onChange={(e) => onItemChange(r.key, 'unit', e.target.value)} />
      ),
    },
    {
      title: '单价(¥)', key: 'unitPrice', width: 110,
      render: (_, r) => (
        <InputNumber min={0} precision={2} value={r.unitPrice}
          onChange={(v) => onItemChange(r.key, 'unitPrice', v || 0)}
          style={{ width: '100%' }} />
      ),
    },
    {
      title: '小计(¥)', key: 'subtotal', width: 120,
      render: (_, r) => {
        const st = Math.round((r.quantity || 0) * (r.unitPrice || 0) * 100) / 100;
        return <Text strong>{st.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>;
      },
    },
    {
      title: '操作', key: 'actions', width: 60,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeItemRow(r.key)} />
      ),
    },
  ];

  const paymentColumns = [
    {
      title: '比例', key: 'percentage', width: 180,
      render: (_, r) => (
        <Slider min={0} max={100} value={r.percentage}
          onChange={(v) => onPaymentChange(r.key, 'percentage', v)}
          tooltip={{ formatter: (v) => `${v}%` }}
          style={{ width: 120 }}
        />
      ),
    },
    {
      title: '金额(¥)', key: 'amount', width: 120,
      render: (_, r) => (
        <InputNumber min={0} precision={2}
          value={r.amount} style={{ width: '100%' }}
          disabled />
      ),
    },
    {
      title: '方式', key: 'method', width: 120,
      render: (_, r) => (
        <Select value={r.method} style={{ width: '100%' }}
          onChange={(v) => onPaymentChange(r.key, 'method', v)}>
          {['银行转账', '支票', '微信', '支付宝', '现金'].map((m) => (
            <Select.Option key={m} value={m}>{m}</Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: '计划日期', key: 'plannedDate', width: 140,
      render: (_, r) => (
        <DatePicker value={r.plannedDate ? dayjs(r.plannedDate) : null}
          onChange={(v) => onPaymentChange(r.key, 'plannedDate', v)}
          style={{ width: '100%' }} />
      ),
    },
    {
      title: '备注', key: 'notes', width: 120,
      render: (_, r) => (
        <Input value={r.notes}
          onChange={(e) => onPaymentChange(r.key, 'notes', e.target.value)} />
      ),
    },
    {
      title: '操作', key: 'actions', width: 60,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />}
          onClick={() => removePaymentRow(r.key)} />
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Title level={4}>{fromQuotationId ? '从报价转化合同' : '新建合同'}</Title>

      <Form form={form} layout="vertical" onFinish={handleSubmit}
        initialValues={{ type: '产品销售合同' }}>
        {/* 基本信息 */}
        <Card size="small" title="基本信息" style={{ marginBottom: 16 }}>
          <Space style={{ width: '100%' }} size="large" wrap>
            <Form.Item name="customerId" label="客户" rules={[{ required: true, message: '请选择客户' }]}
              style={{ minWidth: 260 }}>
              <Select showSearch placeholder="搜索客户"
                filterOption={(input, option) =>
                  (option.children || '').toLowerCase().includes(input.toLowerCase())
                }>
                {customers.map((c) => (
                  <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="type" label="合同类型" rules={[{ required: true }]} style={{ minWidth: 160 }}>
              <Select>
                {CONTRACT_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="signedAt" label="签署日期" style={{ minWidth: 160 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="startAt" label="开始日期" style={{ minWidth: 160 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="endAt" label="结束日期" style={{ minWidth: 160 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item label="附件">
            <Space>
              <Input placeholder="附件路径（如 /uploads/contract-001.pdf）"
                value={attachment} onChange={(e) => setAttachment(e.target.value)}
                style={{ width: 400 }} />
              <Upload beforeUpload={() => false} showUploadList={false}>
                <Button icon={<UploadOutlined />}>选择文件</Button>
              </Upload>
              <Text type="secondary" style={{ fontSize: 12 }}>输入路径或选择文件</Text>
            </Space>
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="合同备注（可选）" />
          </Form.Item>
        </Card>

        {/* 产品明细 */}
        <Card size="small" title="产品明细"
          extra={<Button size="small" icon={<PlusOutlined />} onClick={addItemRow}>添加项目</Button>}
          style={{ marginBottom: 16 }}>
          <Table columns={itemColumns} dataSource={items} rowKey="key"
            pagination={false} size="small" />

          <Divider />
          <div style={{ textAlign: 'right' }}>
            <Text style={{ fontSize: 16 }}>
              合同总额：
              <Text strong style={{ fontSize: 20, color: '#ff4d4f' }}>
                {itemsTotal.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' })}
              </Text>
            </Text>
          </div>
        </Card>

        {/* 付款计划 */}
        <Card size="small" title="付款计划"
          extra={<Button size="small" icon={<PlusOutlined />} onClick={addPaymentRow}>添加阶段</Button>}
          style={{ marginBottom: 16 }}>
          <Table columns={paymentColumns} dataSource={paymentPlan} rowKey="key"
            pagination={false} size="small" />
        </Card>

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => navigate('/contracts')}>取消</Button>
            <Button type="primary" htmlType="submit" loading={submitting}
              style={{ background: '#07c160', borderColor: '#07c160' }}>
              保存合同
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
}
